import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import Loader from '@/components/Loader';
import TeamLogoUploader from '@/components/TeamLogoUploader';
import { createTeamForUser } from '@/services/teamService';
import { createDefaultSeason } from '@/services/seasonService';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import TeamSubNav from '@/components/team/TeamSubNav';

/* ------------------ SAME OPTIONS AS CREATE TEAM ------------------
 * Sports + age groups have to cover every sport CreateTeams.jsx
 * supported (Soccer, Basketball, Baseball, Softball, Football,
 * Volleyball, Lacrosse, Hockey, Other), otherwise users coming from
 * a "Create Team" CTA in another sport find their option missing.
 * Where US youth sports use sport-specific age systems (Hockey's
 * Mites/Squirts/etc., Basketball's grade levels) we use those.
 * Sports without a strong canonical system fall back to U-numbers. */
const sportsAndAgeGroups = {
  Soccer: [
    'U6','U7','U8','U9','U10','U11','U12','U13','U14','U15','U16','U17','U18','U19','Adult'
  ],
  Basketball: [
    '3rd Grade','4th Grade','5th Grade','6th Grade','7th Grade','8th Grade','High School','Adult'
  ],
  Baseball: [
    '8U','9U','10U','11U','12U','13U','14U','High School','Adult'
  ],
  Softball: [
    '8U','9U','10U','11U','12U','13U','14U','High School','Adult'
  ],
  Football: [
    'U8','U9','U10','U11','U12','U13','U14','Junior Varsity','Varsity','Adult'
  ],
  Volleyball: [
    'U10','U11','U12','U13','U14','U15','U16','U17','U18','Adult'
  ],
  Lacrosse: [
    'U9','U10','U11','U12','U13','U14','U15','U16','U17','U18','U19','Adult'
  ],
  Hockey: [
    'Mites (8U)','Squirts (10U)','Pee Wee (12U)','Bantam (14U)','Midget (18U)','Adult'
  ],
  Other: [
    'Youth','Teen','High School','Adult','Mixed'
  ],
};
const sportTypes = Object.keys(sportsAndAgeGroups);
const genderOptions = ['Girls','Boys','Coed'];

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const BODY_FONT = "'Barlow', sans-serif";

// Authenticated routes use the light in-app theme. Pre-auth pages
// (Login / Register / ForgotPassword / Pricing) use the dark
// lime/pitch theme; this wizard is post-auth, so it lives on the
// soft lime-tinted off-white background like Dashboard, Roster, etc.
const labelClass =
  'text-[12px] font-bold uppercase tracking-[1.2px] text-zinc-600';

const inputClass =
  'w-full min-h-[48px] rounded-[10px] border border-zinc-200 bg-white px-4 py-3 text-[15px] text-[#0d1f14] placeholder:text-zinc-400 transition-colors focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-500';

/**
 * TeamSetupWizard — canonical create + finish-setup flow.
 *
 * Mounted at TWO routes:
 *   - /teams/create        → "new team" mode. No teamId in URL. Shows
 *                            only the Basics section. Saving Basics
 *                            calls createTeamForUser, then redirects
 *                            to /teams/{newId}/setup so the user can
 *                            continue with League/Location/Branding.
 *   - /teams/:teamId/setup → "edit team" mode. Loads the existing
 *                            team doc and shows all four sections.
 *
 * Why one component for both? Coaches were getting two wildly
 * different experiences depending on entry point ("Create Team"
 * landed on a separate basic form with no progress UI). Folding
 * both flows here means the lime/pitch design + progress bar +
 * section completion live in one place.
 */
/* ---------- Wizard copy (bilingual, 2026-07-24 audit 3a) ----------
 * The wizard is the first post-auth surface a coach meets; it was 100%
 * hardcoded English while the rest of the coach app followed the app
 * language. Local dict pattern (same as RegisterPage's T) — option
 * VALUES stay English (stored data); only display labels translate.
 * Age-group codes (U11, 8U, grades) stay as-is. */
const WIZARD_STRINGS = {
  en: {
    newTeam: 'New team', almostReady: 'Almost ready',
    titleCreate: ['Create your', 'team.'], titleFinish: ['Finish team', 'setup.'],
    subCreate: "Start with the basics. We'll guide you through the rest after.",
    subFinish: 'Just the essentials, you can edit any of this later.',
    secBasics: 'Basics', secLocation: 'Location', secBranding: 'Branding',
    optionalTag: '(optional)',
    sectionsComplete: (n, total) => `${n} of ${total} sections complete`,
    badgeComplete: 'Complete', badgeOptional: 'Optional', badgeIncomplete: 'Incomplete',
    badgeDoneForYou: 'Done for you',
    teamName: 'Team name', teamNamePh: 'e.g., Springfield Strikers U12',
    sportType: 'Sport type', selectSport: 'Select sport',
    ageGroup: 'Age group', selectAge: 'Select age group', selectSportFirst: 'Select sport first',
    gender: 'Gender (optional)', select: 'Select',
    sportNames: {},
    genderNames: {},
    locationHelp: 'Optional. Your home city powers weather alerts for practices and games. Add it anytime.',
    homeCity: 'Home city', homeCityPh: 'e.g., Rancho Cucamonga',
    region: 'Region / state', regionPh: 'e.g., CA',
    teamLogo: 'Team logo',
    logoHelp: 'Optional — you can add a logo later from team settings.',
    primaryColor: 'Primary color',
    colorTip: 'Tip: pick a color that matches your team logo above.',
    saveSection: 'Save section', creating: 'Creating…', createContinue: 'Create team & continue',
    uploading: 'Uploading…',
    addLater: "I'll add this later", finishLater: "I'll finish later",
    cancel: 'Cancel', finishSetup: 'Finish setup', completeRequired: 'Complete required fields',
    comeBack: 'You can come back anytime.',
    newTeamFooter: "After you create the team you'll add location and branding details next.",
    tTeamNotFound: 'Team not found', tLoadFailed: 'Failed to load team.',
    tSignInFirst: 'Please sign in first', tCreatingTeam: 'Creating your team…',
    tTeamCreated: 'Team created. Keep going to finish setup.',
    tCreateFailed: 'Could not create team. Please try again.',
    tBasicsSaved: 'Basics saved', tBasicsFailed: 'Could not save basics',
    tLocationSaved: 'Location saved', tLocationFailed: 'Could not save location',
    tBrandingSaved: 'Branding saved', tBrandingFailed: 'Could not save branding',
    tCompleteRequired: 'Please complete all required fields',
    tSetupComplete: 'Team setup complete!', tSetupFailed: 'Could not complete setup',
  },
  es: {
    newTeam: 'Equipo nuevo', almostReady: 'Casi listo',
    titleCreate: ['Crea tu', 'equipo.'], titleFinish: ['Termina la', 'configuración.'],
    subCreate: 'Empieza con lo básico. Te guiamos con el resto después.',
    subFinish: 'Solo lo esencial, puedes editar todo esto después.',
    secBasics: 'Básicos', secLocation: 'Ubicación', secBranding: 'Imagen',
    optionalTag: '(opcional)',
    sectionsComplete: (n, total) => `${n} de ${total} secciones completas`,
    badgeComplete: 'Completa', badgeOptional: 'Opcional', badgeIncomplete: 'Incompleta',
    badgeDoneForYou: 'Lista por ti',
    teamName: 'Nombre del equipo', teamNamePh: 'p. ej., Tigres del Valle U12',
    sportType: 'Deporte', selectSport: 'Elige el deporte',
    ageGroup: 'Categoría', selectAge: 'Elige la categoría', selectSportFirst: 'Elige el deporte primero',
    gender: 'Rama (opcional)', select: 'Elegir',
    sportNames: {
      Soccer: 'Fútbol', Basketball: 'Básquetbol', Baseball: 'Béisbol',
      Softball: 'Sóftbol', Football: 'Fútbol americano', Volleyball: 'Vóleibol',
      Lacrosse: 'Lacrosse', Hockey: 'Hockey', Other: 'Otro',
    },
    genderNames: { Girls: 'Niñas', Boys: 'Niños', Coed: 'Mixto' },
    locationHelp: 'Opcional. Tu ciudad activa las alertas del clima para entrenamientos y partidos. Agrégala cuando quieras.',
    homeCity: 'Ciudad', homeCityPh: 'p. ej., Rancho Cucamonga',
    region: 'Región / estado', regionPh: 'p. ej., CA',
    teamLogo: 'Logo del equipo',
    logoHelp: 'Opcional — puedes agregar un logo después desde la configuración del equipo.',
    primaryColor: 'Color principal',
    colorTip: 'Consejo: elige un color que combine con el logo de tu equipo.',
    saveSection: 'Guardar sección', creating: 'Creando…', createContinue: 'Crear equipo y continuar',
    uploading: 'Subiendo…',
    addLater: 'Lo agrego después', finishLater: 'Lo termino después',
    cancel: 'Cancelar', finishSetup: 'Terminar configuración', completeRequired: 'Completa los campos obligatorios',
    comeBack: 'Puedes volver cuando quieras.',
    newTeamFooter: 'Después de crear el equipo agregarás la ubicación y la imagen.',
    tTeamNotFound: 'Equipo no encontrado', tLoadFailed: 'No se pudo cargar el equipo.',
    tSignInFirst: 'Primero inicia sesión', tCreatingTeam: 'Creando tu equipo…',
    tTeamCreated: 'Equipo creado. Sigue para terminar la configuración.',
    tCreateFailed: 'No se pudo crear el equipo. Inténtalo de nuevo.',
    tBasicsSaved: 'Básicos guardados', tBasicsFailed: 'No se pudieron guardar los básicos',
    tLocationSaved: 'Ubicación guardada', tLocationFailed: 'No se pudo guardar la ubicación',
    tBrandingSaved: 'Imagen guardada', tBrandingFailed: 'No se pudo guardar la imagen',
    tCompleteRequired: 'Completa todos los campos obligatorios',
    tSetupComplete: '¡Configuración del equipo completa!', tSetupFailed: 'No se pudo completar la configuración',
  },
};

export default function TeamSetupWizard() {
  const { teamId: routeTeamId } = useParams();
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  // t() feeds the auto season's stored name; W drives the wizard UI copy.
  const { t, i18n } = useTranslation();
  const W = WIZARD_STRINGS[String(i18n?.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'];

  // In "new team" mode the URL has no :teamId. We carry the freshly-
  // created id in local state until the redirect lands.
  const isNewTeam = !routeTeamId;
  const [createdTeamId, setCreatedTeamId] = useState(null);
  const teamId = routeTeamId || createdTeamId;

  // In new-team mode we don't need to wait for any fetch.
  const [loading, setLoading] = useState(!isNewTeam);
  const [team, setTeam] = useState(null);
  const [creatingTeam, setCreatingTeam] = useState(false);

  const [name, setName] = useState('');
  const [sportType, setSportType] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [gender, setGender] = useState('');

  // primaryLeague used to live in the wizard as a required "League"
  // section. Dropped because (a) it was a vestige of the scrimmage-
  // matching era when knowing each team's league mattered for
  // opponent-matching, and (b) brand-new coaches don't know what to
  // put there before they've even built their team. The field is
  // still present on the team doc and capturable from EditTeam, just
  // not gated on creation.
  const [homeCity, setHomeCity] = useState('');
  const [region, setRegion] = useState('');

  const [primaryColor, setPrimaryColor] = useState('#b5f03d');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);

  const availableAgeGroups = sportsAndAgeGroups[sportType] || [];

  const onSportChange = useCallback((e) => {
    const newSport = e.target.value;
    setSportType(newSport);
    const validAges = sportsAndAgeGroups[newSport] || [];
    if (ageGroup && !validAges.includes(ageGroup)) {
      setAgeGroup('');
    }
  }, [ageGroup]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // New-team mode: nothing to load yet. Stay on Basics until the
      // user fills it out and we create the team on first save.
      if (isNewTeam) {
        setLoading(false);
        return;
      }
      if (!teamId) return;
      try {
        const snap = await getDoc(doc(db, 'teams', teamId));
        if (!snap.exists()) {
          toast.error(W.tTeamNotFound);
          navigate('/teams', { replace: true });
          return;
        }
        const t = snap.data();
        if (!cancelled) {
          setTeam({ id: snap.id, ...t });
          setName(t.name || '');
          setSportType(t.sportType || t.sport || '');
          setAgeGroup(t.ageGroup || '');
          setGender(t.gender || '');
          setHomeCity(t.city || t.homeCity || '');
          setRegion(t.region || t.state || '');
          const color = t.branding?.primaryColor || t.primaryColor || '#b5f03d';
          setPrimaryColor(color);
          setLogoUrl(t.logoUrl || t.branding?.logoUrl || '');
        }
      } catch (e) {
        console.error('[TeamSetupWizard] load failed', e);
        toast.error(W.tLoadFailed);
        navigate('/teams', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [teamId, navigate]);

  const basicsComplete   = !!name && !!sportType && !!ageGroup;
  const locationComplete = !!homeCity && !!region;
  // Logo is optional — many new coaches don't have one ready at signup
  // and forcing one was a real friction point. Color stays required
  // because the UI tints (event cards, accents) need it. Coach can
  // upload a logo any time later from EditTeam. May 2026.
  const brandingComplete = !!primaryColor;

  // Location is OPTIONAL for completion (July 2026): it powers weather
  // alerts, which are a post-activation concern — nothing in the
  // messaging path needs it, and required-looking-but-skippable fields
  // were a live friction finding (a coach could not tell if they were
  // blocked). The fields stay in the wizard as a plainly-optional
  // section; the extras ladder and EditTeam pick it up later.
  const allComplete = basicsComplete && brandingComplete;

  async function saveBasics() {
    // Branch 1: NEW TEAM mode — Basics is the team-creation form.
    // Call the canonical createTeamForUser service (same one
    // CreateTeams.jsx used) so we get all the side-effects: team
    // doc, members subcollection, ownership, etc. Then redirect to
    // the setup URL with the new id so subsequent sections work.
    if (isNewTeam) {
      if (!firebaseUser?.uid) {
        toast.error(W.tSignInFirst);
        return;
      }
      setCreatingTeam(true);
      const toastId = toast.loading(W.tCreatingTeam);
      try {
        // createTeamForUser returns { teamId } (an object), not the id
        // string. Destructure so we don't end up navigating to
        // /teams/[object%20Object]/setup which fails the doc read and
        // redirects the user back to /teams with a "Team not found"
        // toast.
        const { teamId: newTeamId } = await createTeamForUser({
          uid: firebaseUser.uid,
          name: name.trim(),
          sport: sportType,
          ageGroup,
          gender: gender || null,
          extraTeamFields: {
            // Match the field name the wizard reads on load
            sportType,
            setupProgress: { basics: true },
          },
        });
        // Auto-default season (features/auto-default-season/): created
        // HERE, in the same action as the team, not at finishSetup —
        // the wizard remounts on the redirect below so "did this
        // session create the team" state wouldn't survive, and this
        // call site is structurally the wizard-new-team path only
        // (club/bulk flows and wizard edit mode never run it). Failure
        // is soft (A8): a coach with no season just sees checklist
        // step 2 pending, exactly today's experience.
        try {
          await createDefaultSeason({ teamId: newTeamId, uid: firebaseUser.uid, t });
        } catch (seasonErr) {
          console.warn('[Wizard] default season create failed', seasonErr?.message);
        }
        setCreatedTeamId(newTeamId);
        toast.success(W.tTeamCreated, { id: toastId });
        // Replace history entry so the back button doesn't return to
        // an empty /teams/create form.
        navigate(`/teams/${newTeamId}/setup`, { replace: true });
      } catch (e) {
        console.error('[Wizard] team creation failed', e);
        toast.error(W.tCreateFailed, { id: toastId });
      } finally {
        setCreatingTeam(false);
      }
      return;
    }

    // Branch 2: EDIT mode — patch the existing team's basics.
    // setDoc(merge:true) deep-merges nested maps, so `setupProgress`
    // here only updates the `basics` key and leaves location/branding
    // flags intact. (updateDoc with a `setupProgress: {...}` literal
    // REPLACES the whole map, which was wiping the other sections'
    // progress.) Write both `sport` and `sportType` so they never drift.
    try {
      await setDoc(
        doc(db, 'teams', teamId),
        {
          name: name.trim(),
          sport: sportType,
          sportType,
          ageGroup,
          gender: gender || null,
          setupProgress: { basics: true },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success(W.tBasicsSaved);
    } catch (e) {
      console.error('[Wizard] basics save failed', e);
      toast.error(W.tBasicsFailed);
    }
  }

  async function saveLocation() {
    try {
      await setDoc(
        doc(db, 'teams', teamId),
        {
          homeCity,
          city: homeCity,
          region,
          state: region,
          setupProgress: { location: true },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success(W.tLocationSaved);
    } catch (e) {
      console.error('[Wizard] location save failed', e);
      toast.error(W.tLocationFailed);
    }
  }

  async function saveBranding() {
    try {
      await setDoc(
        doc(db, 'teams', teamId),
        {
          primaryColor,
          branding: { primaryColor, logoUrl: logoUrl || null },
          setupProgress: { branding: !!primaryColor && !!logoUrl },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success(W.tBrandingSaved);
    } catch (e) {
      console.error('[Wizard] branding save failed', e);
      toast.error(W.tBrandingFailed);
    }
  }

  // Explicit "I'll finish later" / "I'll add this later" — the coach
  // deliberately defers, so honor it: clear the mustCompleteTeamId nudge
  // so the TeamSetupNotice banner doesn't keep nagging about a team they
  // chose to set up later. (They can always finish from team settings;
  // accidental navigation away — not via these links — still leaves the
  // gentle reminder in place.)
  async function skipForNow() {
    if (firebaseUser?.uid && teamId) {
      try {
        await updateDoc(doc(db, 'users', firebaseUser.uid), {
          mustCompleteTeamId: deleteField(),
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        // Non-fatal — worst case the banner shows once more.
        console.warn('[Wizard] skipForNow flag clear failed', e?.code);
      }
    }
    navigate(`/teams/${teamId}`);
  }

  async function finishSetup() {
    if (!firebaseUser?.uid) return;
    if (!allComplete) {
      toast.error(W.tCompleteRequired);
      return;
    }
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        setupComplete: true,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        mustCompleteTeamId: deleteField(),
        updatedAt: serverTimestamp(),
      });
      toast.success(W.tSetupComplete);
      navigate(`/teams/${teamId}?welcome=1`, { replace: true });
    } catch (e) {
      console.error('[Wizard] finish failed', e);
      toast.error(W.tSetupFailed);
    }
  }

  if (loading) {
    return (
      <div className="min-h-dvh grid place-items-center bg-[#f7faf5]">
        <Loader />
      </div>
    );
  }

  return (
    <div
      className="relative min-h-dvh bg-[#f7faf5] text-[#0d1f14]"
      style={{ fontFamily: BODY_FONT }}
    >
      <div className="relative z-10 mx-auto max-w-3xl px-5 py-10 sm:py-14">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={teamId} />
        {/* Header — copy adapts to mode so the user knows whether
            they're starting fresh (new) or finishing setup (edit). */}
        <div className="mb-7 text-center sm:text-left">
          <span className="inline-flex items-center rounded-full border border-[#5a8a1f]/30 bg-[#b5f03d]/15 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.5px] text-[#5a8a1f]">
            {isNewTeam ? W.newTeam : W.almostReady}
          </span>
          <h1
            className="mt-4 uppercase leading-none text-[#0d1f14]"
            style={{
              fontFamily: HEAD_FONT,
              fontWeight: 900,
              fontSize: 'clamp(30px, 4vw, 42px)',
              letterSpacing: '-0.5px',
            }}
          >
            {isNewTeam ? (
              <>{W.titleCreate[0]} <span className="text-[#5a8a1f]">{W.titleCreate[1]}</span></>
            ) : (
              <>{W.titleFinish[0]} <span className="text-[#5a8a1f]">{W.titleFinish[1]}</span></>
            )}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-zinc-600">
            {isNewTeam ? W.subCreate : W.subFinish}
          </p>
        </div>

        {/* Progress indicator: shows section completion at-a-glance.
            In new-team mode we display it differently — only Basics
            is reachable, so the bar shows 1-of-4 plus a hint that
            more is coming. */}
        <ProgressBar
          W={W}
          steps={[
            { label: W.secBasics,   complete: basicsComplete },
            { label: W.secLocation, complete: locationComplete, optional: true },
            { label: W.secBranding, complete: brandingComplete },
          ]}
        />

        {/* BASICS */}
        <Card title={W.secBasics} complete={basicsComplete} W={W}>
          <Field label={W.teamName} required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={W.teamNamePh}
              className={inputClass}
            />
          </Field>

          <Field label={W.sportType} required>
            <select value={sportType} onChange={onSportChange} className={inputClass}>
              <option value="" disabled>{W.selectSport}</option>
              {sportTypes.map(s => <option key={s} value={s}>{W.sportNames[s] || s}</option>)}
            </select>
          </Field>

          <Field label={W.ageGroup} required>
            <select
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
              disabled={!sportType}
              className={inputClass}
            >
              <option value="" disabled>{sportType ? W.selectAge : W.selectSportFirst}</option>
              {availableAgeGroups.map(age => <option key={age} value={age}>{age}</option>)}
            </select>
          </Field>

          <Field label={W.gender}>
            <select value={gender} onChange={(e) => setGender(e.target.value)} className={inputClass}>
              <option value="">{W.select}</option>
              {genderOptions.map(g => <option key={g} value={g}>{W.genderNames[g] || g}</option>)}
            </select>
          </Field>

          <SaveButton
            onClick={saveBasics}
            disabled={!basicsComplete || creatingTeam}
          >
            {isNewTeam
              ? (creatingTeam ? W.creating : W.createContinue)
              : W.saveSection}
          </SaveButton>
        </Card>

        {/* In new-team mode we hide Location/Branding entirely.
            They become reachable after the team is created (the user
            is redirected to /teams/{newId}/setup which renders them).
            Showing them now would be misleading — they require an
            existing teamId to save against. */}
        {!isNewTeam && (
        <>
        {/* LOCATION — optional: powers weather alerts, not messaging.
            Deliberately unstarred so a coach can tell at a glance that
            nothing here blocks them (live friction finding, 2026-07-22). */}
        <Card title={W.secLocation} complete={locationComplete} optional W={W}>
          <p className="-mt-1 text-xs text-zinc-500">
            {W.locationHelp}
          </p>
          <Field label={W.homeCity}>
            <input
              type="text"
              value={homeCity}
              onChange={(e) => setHomeCity(e.target.value)}
              placeholder={W.homeCityPh}
              className={inputClass}
            />
          </Field>
          <Field label={W.region}>
            <input
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder={W.regionPh}
              className={inputClass}
            />
          </Field>
          <div className="flex items-center gap-3 flex-wrap">
            <SaveButton onClick={saveLocation} disabled={!locationComplete}>{W.saveSection}</SaveButton>
            {/* Per-section skip link — gives coaches a clear escape
                hatch when they don't have address handy at signup.
                The bottom-of-wizard "I'll finish later" already exists
                but isn't visible without scrolling, and the wizard's
                vertical layout buries it. Repeating the affordance
                next to each section's save button matches the
                ParentOnboarding pattern (skip on every step). */}
            <button
              type="button"
              onClick={skipForNow}
              className="text-sm font-semibold text-zinc-500 hover:text-zinc-800 underline-offset-4 hover:underline transition-colors"
            >
              {W.addLater}
            </button>
          </div>
        </Card>

        {/* BRANDING — logo first, then color. Reading order matters
            here: a coach uploads their team's crest, can SEE it, and
            then picks a primary color that matches. Asking for a
            color before the logo means picking blind. */}
        {/* Branding is born "complete" (the primary color defaults to
            Scrimly lime, logo is optional). Saying "Complete" for a
            section the coach never touched read as a bug (UAT
            2026-07-24) — the badge says "Done for you" until they
            actually customize something. */}
        <Card title={W.secBranding} complete={brandingComplete}
          completeLabel={(!logoUrl && primaryColor === '#b5f03d') ? W.badgeDoneForYou : undefined}
          W={W}>
          <Field label={W.teamLogo}>
            <TeamLogoUploader
              teamId={teamId}
              value={logoUrl}
              onChange={setLogoUrl}
              onUploadingChange={setLogoUploading}
            />
            <p className="mt-1.5 text-xs text-zinc-500">
              {W.logoHelp}
            </p>
          </Field>

          <Field label={W.primaryColor} required>
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="h-12 w-12 cursor-pointer rounded-[10px] border border-zinc-200 bg-white"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                aria-label="Team primary color"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#b5f03d"
                maxLength={7}
                className={inputClass}
              />
            </div>
            {logoUrl && (
              <p className="mt-1.5 text-xs text-zinc-500">
                {W.colorTip}
              </p>
            )}
          </Field>

          <div className="flex items-center gap-3 flex-wrap">
            <SaveButton onClick={saveBranding} disabled={logoUploading || !brandingComplete}>
              {logoUploading ? W.uploading : W.saveSection}
            </SaveButton>
            <button
              type="button"
              onClick={skipForNow}
              className="text-sm font-semibold text-zinc-500 hover:text-zinc-800 underline-offset-4 hover:underline transition-colors"
            >
              {W.addLater}
            </button>
          </div>
        </Card>

        {/* Footer actions */}
        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={skipForNow}
            className="text-sm font-semibold text-zinc-500 hover:text-zinc-800 transition-colors"
            title={W.comeBack}
          >
            {W.finishLater}
          </button>
          <button
            type="button"
            onClick={finishSetup}
            disabled={!allComplete}
            className="inline-flex min-h-[52px] items-center justify-center rounded-[10px] bg-[#b5f03d] px-6 py-4 uppercase tracking-[0.5px] text-[#0d1f14] transition-all hover:bg-[#94cc28] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(181,240,61,0.25)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16 }}
          >
            {allComplete ? W.finishSetup : W.completeRequired}
          </button>
        </div>
        </>
        )}

        {/* New-team mode footer: just an escape hatch + reassurance
            that the next steps come after team creation. */}
        {isNewTeam && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => navigate('/teams')}
              className="text-sm font-semibold text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              {W.cancel}
            </button>
            <p className="mt-4 text-xs text-zinc-400">
              {W.newTeamFooter}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- ProgressBar ---------- */

// Visual completion indicator at the top of the wizard. Shows a
// numbered chip per step, lime-filled when complete, and a thin
// progress bar showing percentage done. Helps coaches see how
// much remains and which sections they've already saved.
function ProgressBar({ steps, W }) {
  // Optional sections (Location) show as chips but never count toward the
  // header or the percentage — otherwise a coach who rightly skips an
  // optional section reads "2 of 3, 67%" forever next to a Finish button
  // that works (funnel-walk finding #2, final crumb).
  const required = steps.filter((s) => !s.optional)
  const completedCount = required.filter((s) => s.complete).length
  const total = required.length
  const pct = Math.round((completedCount / total) * 100)

  return (
    <div className="mb-7">
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {W.sectionsComplete(completedCount, total)}
        </span>
        <span className="text-[11px] font-bold text-zinc-500">{pct}%</span>
      </div>

      {/* Filled bar */}
      <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden mb-3">
        <div
          className="h-full bg-[#b5f03d] transition-all duration-300 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Per-step chips */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        {steps.map((s, i) => (
          <div
            key={s.label}
            className={[
              'flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors',
              s.complete
                ? 'border-[#5a8a1f]/40 bg-[#b5f03d]/15 text-[#5a8a1f]'
                : 'border-zinc-200 bg-white text-zinc-400',
            ].join(' ')}
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            {s.complete && <CheckCircleIcon className="h-3 w-3" />}
            <span className="hidden sm:inline">
              {s.label}
              {s.optional && !s.complete && (
                <span className="ml-1 font-normal normal-case tracking-normal text-zinc-400">{W.optionalTag}</span>
              )}
            </span>
            <span className="sm:hidden">{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ---------- Card / Field / SaveButton ---------- */

function Card({ title, complete, optional, children, completeLabel, W = WIZARD_STRINGS.en }) {
  return (
    <div className="mb-5 rounded-2xl border border-zinc-200 bg-white p-6 sm:p-8 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <h3
          className="uppercase text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 18, letterSpacing: '0.5px' }}
        >
          {title}
        </h3>
        {complete ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#b5f03d]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#5a8a1f]">
            <CheckCircleIcon className="h-3.5 w-3.5" />
            {completeLabel || W.badgeComplete}
          </span>
        ) : optional ? (
          <span className="inline-flex items-center rounded-full bg-zinc-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
            {W.badgeOptional}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-zinc-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
            {W.badgeIncomplete}
          </span>
        )}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>
        {label} {required && <span className="text-[#5a8a1f]">*</span>}
      </label>
      {children}
    </div>
  );
}

function SaveButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[44px] items-center justify-center rounded-[10px] bg-[#b5f03d] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-[#0d1f14] transition-all hover:bg-[#94cc28] active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#b5f03d]"
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
    >
      {children}
    </button>
  );
}
